import { type JSX, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonGroup } from '@heroui/react';
import { Clock, GitBranch, HelpCircle, History, LayoutGrid, List, Plus } from 'lucide-react';

import { useSetPageChrome } from '~/contexts/PageChromeContext';
import { useProductFiltering } from '~/hooks/useProductFiltering';
import { useProducts } from '~/hooks/useProducts';
import { useUnlinkedRepositories } from '~/hooks/useRepositories';
import { useUIStore } from '~/stores/uiStore';

import { Input } from '~/components/Field';
import { MAIN_CONTENT_WIDTH } from '~/components/layout/mainContent';
import { Tooltip } from '~/components/Tooltip';

import type { Product } from '~/types';

import { HelpProductModal } from './modals/HelpProductModal';
import { NewProductModal } from './modals/NewProductModal';
import { RepositoriesModal } from './modals/RepositoriesModal';
import { ProductCard } from './ProductCard';
import { UnlinkedReposBanner } from './UnlinkedReposBanner';

/** Which single header/banner-triggered modal is currently open, if any. */
type ActiveModal = 'help' | 'new-product' | 'repositories' | 'unlinked-repositories' | null;

function matchesSearch(product: Product, query: string): boolean {
  if (!query.trim()) return true;
  const haystack = `${product.name} ${product.description ?? ''}`.toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

/** Stable reference — the Products list has a fixed title and no breadcrumb trail. */
const PRODUCTS_CHROME = { title: 'Products' };

/**
 * Main landing container for /products: header actions, the unlinked-repos
 * banner, search + view-mode filter bar, and the product grid/list itself.
 *
 * Container — owns data fetching (useProducts, useUnlinkedRepositories),
 * search/modal state, and passes resolved props down to display components
 * (ProductCard, UnlinkedReposBanner).
 * Pattern: docs/design-patterns/frontend/component-patterns.md
 */
export function ProductGrid(): JSX.Element {
  useSetPageChrome(PRODUCTS_CHROME);

  const navigate = useNavigate();
  const { data: products, isError, isLoading } = useProducts();
  const { data: unlinkedRepos } = useUnlinkedRepositories();
  const viewMode = useUIStore(s => s.productsViewMode);
  const setViewMode = useUIStore(s => s.setProductsViewMode);
  const { pinnedProductIds, setSortOption, sortedProducts, sortOption, togglePin } =
    useProductFiltering(products ?? []);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  const filteredProducts = useMemo(
    () => sortedProducts.filter(product => matchesSearch(product, searchQuery)),
    [sortedProducts, searchQuery],
  );

  const totalCount = products?.length ?? 0;
  const closeModal = (): void => setActiveModal(null);

  return (
    <div className={`space-y-4 ${MAIN_CONTENT_WIDTH}`}>
      <UnlinkedReposBanner
        unlinkedRepos={unlinkedRepos ?? []}
        onResolve={() => setActiveModal('unlinked-repositories')}
      />

      <div className="flex items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <Button
            startContent={<GitBranch aria-hidden="true" className="h-4 w-4" />}
            variant="flat"
            onPress={() => setActiveModal('repositories')}
          >
            View Repositories
          </Button>
          <Button
            color="primary"
            startContent={<Plus aria-hidden="true" className="h-4 w-4" />}
            onPress={() => setActiveModal('new-product')}
          >
            New Product
          </Button>
          <Button
            isIconOnly
            aria-label="What is a Product?"
            variant="light"
            onPress={() => setActiveModal('help')}
          >
            <HelpCircle aria-hidden="true" className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <Input
          aria-label="Search products"
          className="max-w-xs"
          placeholder="Search products..."
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted">
            {filteredProducts.length} of {totalCount} products
          </span>
          <ButtonGroup size="sm">
            <Tooltip content="Sort by newest first">
              <Button
                isIconOnly
                aria-label="Sort by newest first"
                aria-pressed={sortOption === 'newest'}
                className={sortOption === 'newest' ? 'bg-primary text-primary-foreground' : ''}
                size="sm"
                variant="light"
                onPress={() => setSortOption('newest')}
              >
                <Clock aria-hidden="true" className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip content="Sort by recently updated">
              <Button
                isIconOnly
                aria-label="Sort by recently updated"
                aria-pressed={sortOption === 'recentlyUpdated'}
                className={
                  sortOption === 'recentlyUpdated' ? 'bg-primary text-primary-foreground' : ''
                }
                size="sm"
                variant="light"
                onPress={() => setSortOption('recentlyUpdated')}
              >
                <History aria-hidden="true" className="h-4 w-4" />
              </Button>
            </Tooltip>
          </ButtonGroup>
          <div className="flex items-center gap-1 rounded-md border border-default p-0.5">
            <Button
              isIconOnly
              aria-label="Grid view"
              aria-pressed={viewMode === 'grid'}
              className={viewMode === 'grid' ? 'bg-primary text-primary-foreground' : ''}
              size="sm"
              variant="light"
              onPress={() => setViewMode('grid')}
            >
              <LayoutGrid aria-hidden="true" className="h-4 w-4" />
            </Button>
            <Button
              isIconOnly
              aria-label="List view"
              aria-pressed={viewMode === 'list'}
              className={viewMode === 'list' ? 'bg-primary text-primary-foreground' : ''}
              size="sm"
              variant="light"
              onPress={() => setViewMode('list')}
            >
              <List aria-hidden="true" className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted">Loading products...</p>}
      {isError && (
        <p className="text-sm text-error">Failed to load products. Please try again later.</p>
      )}

      {!isLoading && !isError && totalCount === 0 && (
        <div className="rounded-lg border border-dashed border-default p-8 text-center">
          <p className="text-sm text-muted">
            No products yet. Create one to group your repositories and projects.
          </p>
          <Button
            className="mt-3"
            color="primary"
            startContent={<Plus aria-hidden="true" className="h-4 w-4" />}
            onPress={() => setActiveModal('new-product')}
          >
            New Product
          </Button>
        </div>
      )}

      {!isLoading && !isError && totalCount > 0 && filteredProducts.length === 0 && (
        <p className="text-sm text-muted">No products match &ldquo;{searchQuery}&rdquo;.</p>
      )}

      {!isLoading && !isError && filteredProducts.length > 0 && (
        <div
          className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'
              : 'flex flex-col gap-2'
          }
        >
          {filteredProducts.map(product => (
            <ProductCard
              key={product.id}
              isPinned={pinnedProductIds.has(product.id)}
              product={product}
              viewMode={viewMode}
              onSelect={id => void navigate(`/products/${id}`)}
              onTogglePin={togglePin}
            />
          ))}
        </div>
      )}

      <NewProductModal isOpen={activeModal === 'new-product'} onClose={closeModal} />
      <RepositoriesModal isOpen={activeModal === 'repositories'} onClose={closeModal} />
      <RepositoriesModal
        isOpen={activeModal === 'unlinked-repositories'}
        unlinkedOnly
        onClose={closeModal}
      />
      <HelpProductModal isOpen={activeModal === 'help'} onClose={closeModal} />
    </div>
  );
}
